import { computed, observable, action, isArrayLike } from 'mobx';
import get from 'lodash/get';
import groupBy from 'lodash/groupBy';
import { TListDisclaimersResponseBody } from '@kurtosys/ksys-api-client/dist/models/requests/config/TListDisclaimersResponseBody';
import { utils } from '@kurtosys/ksys-app-template';
import { BaseStore } from './BaseStore';
import { DataStore } from './DataStore';
import { Services } from '../services';
import { Config } from '../models/config';
import { ConditionalDisclaimer } from '../models/configuration/disclaimers/ConditionalDisclaimer';
import { ConditionalDisclaimers } from '../models/configuration/disclaimers/ConditionalDisclaimers';
import { ConditionalHelper } from '@kurtosys/ksys-app-template/dist/helpers';
import { DisclaimerDisplayType } from '../models/configuration/disclaimers/DisclaimerDisplayType';
import { ColumnConfig } from '../models/configuration/explorer/ColumnConfig';
import { DisclaimersByDocumentType } from '../models/configuration/disclaimers/DisclaimersByDocumentType';
import { ConditionalDisclaimersByKey } from '../models/configuration/disclaimers/ConditionalDisclaimersByKey';

interface DisclaimersByFundType {
    [fundType: string]: DisclaimersByModuleId;
}

interface DisclaimersByModuleId {
    [moduleId: string]: DisclaimersByKey;
}

interface DisclaimersByKey {
    [key: string]: Disclaimer;
}

interface Disclaimer {
    key: string;
    type: string;
    text: string;
    superscript?: number;
    order?: number;
}

export class DisclaimerStore extends BaseStore {
    static maxOrder = 999999;
    @observable.ref rawDisclaimers: TListDisclaimersResponseBody = [];
    @observable.ref disclaimersDictionary: { [key: string]: string };
    @observable.ref
    disclaimersByFundType: DisclaimersByFundType = {};
    /**
     * This array indicates what sub properties will be recursive in regards to the method 'getConditionalDisclaimersFromNestedKeyedObject'
     *
     * @private
     */
    private recursiveKeyedProperties: Array<string> = ['disclaimersByCulture'];

    @computed
    get dataStore(): DataStore {
        return this.stores && this.stores.data;
    }
    @computed
    get services(): Services {
        return this.dataStore && this.dataStore.services;
    }
    @computed
    get config(): Config {
        return this.dataStore && this.dataStore.config;
    }
    @computed
    get culture(): string {
        return this.dataStore && this.dataStore.culture;
    }
    @computed
    get disclaimersByKey(): DisclaimersByKey {
        if (this.dataStore) {
            const { config } = this.dataStore;
            const fundType = config.selectedFundType.fundType;
            const moduleId = config.selectedModule.id;
            return (
                this.disclaimersByFundType &&
                this.disclaimersByFundType[fundType] &&
                this.disclaimersByFundType[fundType][moduleId]
            );
        }
        return {};
    }

    @computed
    get conditionalDisclaimerSuperscriptTypes(): ConditionalDisclaimers {
        const response: ConditionalDisclaimers = [];
        if (this.dataStore) {
            const { config } = this.dataStore;
            if (config) {
                const { columnConfig } = config;
                response.push(...this.getConditionalDisclaimersFromFundType());
                response.push(
                    ...this.getConditionalDisclaimersFromDocumentConfig()
                );
                response.push(
                    ...this.getConditionalDisclaimersFromColumnConfig(
                        columnConfig
                    )
                );
            }
        }
        return response.filter(conditionalDisclaimer => {
            const disclaimerDisplayerType =
                conditionalDisclaimer.display ||
                DisclaimerDisplayType.superscript;
            return (
                disclaimerDisplayerType === DisclaimerDisplayType.superscript
            );
        });
    }

    getConditionalDisclaimersFromColumnConfig(
        columnConfig?: Array<ColumnConfig>
    ) {
        const response: ConditionalDisclaimers = [];
        if (isArrayLike(columnConfig) && columnConfig.length > 0) {
            columnConfig.forEach(column => {
                if (column) {
                    const { cellDisclaimers, type } = column;
                    if (isArrayLike(cellDisclaimers)) {
                        response.push(...cellDisclaimers);
                    }
                    if (['group', 'dropdown'].includes(type)) {
                        const { groups = [] } = column;
                        groups.forEach(group => {
                            response.push(
                                ...this.getConditionalDisclaimersFromColumnConfig(
                                    group.columns
                                )
                            );
                        });
                    }
                }
            });
        }
        return response;
    }

    getConditionalDisclaimersFromFundType() {
        const response: ConditionalDisclaimers = [];
        const { config, isMobile } = this.dataStore;
        const { fundTypes } = config;
        if (isArrayLike(fundTypes) && fundTypes.length > 0) {
            fundTypes.forEach(fundType => {
                const { modules } = fundType;
                if (config.selectedFundType === fundType) {
                    response.push(
                        ...this.getConditionalDisclaimersFromModules(
                            modules,
                            isMobile
                        )
                    );
                }
                if (fundType) {
                    let { disclaimers } = fundType;
                    if (isArrayLike(disclaimers)) {
                        if (isMobile) {
                            disclaimers = this.setDisclaimersDisplay(
                                disclaimers,
                                DisclaimerDisplayType.superscript
                            );
                        }
                        response.push(...disclaimers);
                    }
                }
            });
        }
        return response;
    }

    setDisclaimersDisplay(
        disclaimers: ConditionalDisclaimers,
        type: DisclaimerDisplayType
    ) {
        const conditionalDisclaimers = disclaimers.map(disclaimer => {
            return { ...disclaimer, display: type };
        });
        return observable(conditionalDisclaimers);
    }

    getConditionalDisclaimersFromModules(modules?: any, isMobile?: boolean) {
        const response: ConditionalDisclaimers = [];
        if (isArrayLike(modules) && modules.length > 0) {
            modules.forEach(module => {
                if (module) {
                    let { disclaimers } = module;
                    if (isArrayLike(disclaimers)) {
                        if (isMobile) {
                            disclaimers = this.setDisclaimersDisplay(
                                disclaimers,
                                DisclaimerDisplayType.superscript
                            );
                        }
                        response.push(...disclaimers);
                    }
                }
            });
        }
        return response;
    }

    getConditionalDisclaimersByDocumentTypeConfig(): DisclaimersByDocumentType {
        if (this.dataStore) {
            const { config } = this.dataStore;
            if (config) {
                const globalDisclaimers =
                    config.globalDisclaimersByDocumentType;
                const moduleDisclaimers = config.disclaimersByDocumentType;
                return utils.object.deepMergeObjectsWithOptions(
                    {
                        arrayMergeStrategy: 'DeepMergeByKeyOrAppend',
                        getKeyFn: e => `${e.disclaimerType}-${e.display}`,
                    },
                    globalDisclaimers,
                    moduleDisclaimers
                );
            }
        }
    }

    getConditionalDisclaimersForDocument(
        type: string,
        culture?: string
    ): ConditionalDisclaimers {
        if (type) {
            const disclaimersByDocumentType = this.getConditionalDisclaimersByDocumentTypeConfig();
            if (disclaimersByDocumentType && disclaimersByDocumentType[type]) {
                const disclaimersByDocType = disclaimersByDocumentType[type];
                if (
                    culture &&
                    disclaimersByDocType.disclaimersByCulture &&
                    disclaimersByDocType.disclaimersByCulture[culture] &&
                    disclaimersByDocType.disclaimersByCulture[culture]
                        .conditionalDisclaimers
                ) {
                    return disclaimersByDocType.disclaimersByCulture[culture]
                        .conditionalDisclaimers;
                }
                if (
                    disclaimersByDocumentType[type].conditionalDisclaimers &&
                    !culture
                ) {
                    return disclaimersByDocumentType[type]
                        .conditionalDisclaimers;
                }
            }
        }
        return [];
    }

    getConditionalDisclaimersFromDocumentConfig(): ConditionalDisclaimers {
        const disclaimerConfig = this.getConditionalDisclaimersByDocumentTypeConfig();
        const conditionals = this.getConditionalDisclaimersFromNestedKeyedObject(
            disclaimerConfig
        );
        return conditionals;
    }

    getConditionalDisclaimersFromNestedKeyedObject(
        conditionalDisclaimersByKey: ConditionalDisclaimersByKey
    ): ConditionalDisclaimers {
        const conditionalDisclaimers = [];
        if (conditionalDisclaimersByKey) {
            const keys = Object.keys(conditionalDisclaimersByKey);
            keys.forEach(key => {
                const keyDisclaimers = conditionalDisclaimersByKey[key];
                if (keyDisclaimers) {
                    if (keyDisclaimers.conditionalDisclaimers) {
                        conditionalDisclaimers.push(
                            ...keyDisclaimers.conditionalDisclaimers
                        );
                    }
                    this.recursiveKeyedProperties.forEach(prop => {
                        if (keyDisclaimers.hasOwnProperty(prop)) {
                            conditionalDisclaimers.push(
                                ...this.getConditionalDisclaimersFromNestedKeyedObject(
                                    keyDisclaimers[prop]
                                )
                            );
                        }
                    });
                }
            });
        }
        return conditionalDisclaimers;
    }

    @action
    loadDisclaimers() {
        if (this.dataStore) {
            let disclaimersByKey: DisclaimersByKey = {};
            if (this.disclaimersDictionary) {
                Object.keys(this.disclaimersDictionary).forEach(type => {
                    const text = this.disclaimersDictionary[type];
                    disclaimersByKey = this.addDisclaimerToDisclaimersByKey(
                        disclaimersByKey,
                        type,
                        text
                    );
                });
            }

            const { funds, config, isMobile } = this.dataStore;
            funds.forEach(fund => {
                const { disclaimers: fundDisclaimers = {} } = fund;
                Object.keys(fundDisclaimers).forEach(type => {
                    const text = this.getDisclaimerTextFromEntity(fund, type);
                    if (isMobile) {
                        type = DisclaimerDisplayType.superscript;
                    }
                    if (text) {
                        disclaimersByKey = this.addDisclaimerToDisclaimersByKey(
                            disclaimersByKey,
                            type,
                            text
                        );
                    }
                });
            });

            const cellDisclaimers = this.conditionalDisclaimerSuperscriptTypes;
            if (cellDisclaimers.length > 0) {
                funds.forEach(fund => {
                    cellDisclaimers.forEach(cellDisclaimer => {
                        const {
                            conditional,
                            disclaimerType,
                            order,
                        } = cellDisclaimer;
                        const conditionalHelper =
                            conditional && new ConditionalHelper(conditional);

                        const text = this.getDisclaimerTextForType(
                            fund,
                            cellDisclaimer,
                            conditionalHelper
                        );
                        if (text) {
                            const key = this.getDisclaimerKey(
                                disclaimerType,
                                text
                            );
                            if (
                                disclaimersByKey &&
                                disclaimersByKey[key] &&
                                !disclaimersByKey[key].superscript
                            ) {
                                disclaimersByKey[
                                    key
                                ].superscript = this.getNextSuperscript(
                                    disclaimersByKey
                                );
                                disclaimersByKey[key].order =
                                    order || DisclaimerStore.maxOrder;
                            }
                        }
                    });
                });
            }
            this.reorder(disclaimersByKey);
            funds.forEach(fund => {
                fund._disclaimersLoaded = true;
            });
            const currentFundType = config.selectedFundType;
            const currentModule = config.selectedModule;

            if (!this.disclaimersByFundType) {
                this.disclaimersByFundType = {};
            }
            if (!this.disclaimersByFundType[currentFundType.fundType]) {
                this.disclaimersByFundType[currentFundType.fundType] = {};
            }
            this.disclaimersByFundType[currentFundType.fundType][
                currentModule.id
            ] = disclaimersByKey;

            this.disclaimersByFundType = {
                ...this.disclaimersByFundType,
            };
        }
    }

    reorder(disclaimersByKey: DisclaimersByKey) {
        let hasChange = false;
        // Only order superscripted disclaimers
        const values = Object.values(disclaimersByKey).filter(
            v => v.superscript
        );
        const ordersDefined = values.some(
            v => v.order && v.order !== DisclaimerStore.maxOrder
        );
        if (ordersDefined) {
            const currentOrder = values
                .map(v => v.superscript)
                .filter(v => !!v);
            const workingValues = values.sort((a, b) => {
                const aOrder = a.order || -1;
                const bOrder = b.order || -1;
                if (aOrder === bOrder) {
                    return a.superscript < b.superscript
                        ? -1
                        : a.superscript > b.superscript
                        ? 1
                        : 0;
                }
                return aOrder < bOrder ? -1 : 1;
            });
            const newOrder = workingValues
                .map(v => v.superscript)
                .filter(v => !!v);
            for (let i = 0; i < currentOrder.length; i++) {
                const oldValue = currentOrder[i];
                const newValue = newOrder[i];
                if (oldValue !== newValue) {
                    hasChange = true;
                    break;
                }
            }
            if (hasChange) {
                workingValues.forEach((value, index) => {
                    value.superscript = index + 1;
                });
            }
        }
        return hasChange;
    }

    getNextSuperscript(disclaimersByKey: DisclaimersByKey) {
        const nextSuperscript = Object.values(disclaimersByKey).reduce(
            (max, disclaimer) => {
                const { superscript } = disclaimer;
                if (superscript && superscript >= max) {
                    max = disclaimer.superscript + 1;
                }
                return max;
            },
            1
        );
        return nextSuperscript;
    }

    addDisclaimerToDisclaimersByKey(
        disclaimersByKey: DisclaimersByKey,
        type: string,
        text: string,
        addSuperscript: boolean = false
    ) {
        const key = this.getDisclaimerKey(type, text);
        if (!disclaimersByKey[key]) {
            disclaimersByKey[key] = {
                key,
                type,
                text,
            };
        }
        if (addSuperscript && !disclaimersByKey[key].superscript) {
            disclaimersByKey[key].superscript = this.getNextSuperscript(
                disclaimersByKey
            );
        }
        return disclaimersByKey;
    }

    getDisclaimerKey(type: string, text: string) {
        return `${type}_${text}`;
    }

    // Get disclaimers
    @action
    getGlobalDisclaimers = async () => {
        try {
            if (this.services) {
                this.rawDisclaimers = await this.services.getGlobalDisclaimers(
                    this.config &&
                        this.config.globalConfig &&
                        this.config.globalConfig.disclaimerTypes
                );
                const disclaimers = groupBy(
                    this.rawDisclaimers,
                    'disclaimerType'
                );

                for (const d in disclaimers) {
                    if (d) {
                        const disclaimer =
                            disclaimers[d][0].disclaimers.find(
                                disclaimerContent =>
                                    disclaimerContent.culture === this.culture
                            ) ||
                            disclaimers[d][0].disclaimers.find(
                                disclaimerContent =>
                                    disclaimerContent.culture ===
                                    this.config.defaultCulture
                            );

                        disclaimers[d] = get(disclaimer, 'disclaimer', '');
                    }
                }

                this.disclaimersDictionary = disclaimers;
                this.stores.query.loadDefaultContext();
            }
        } catch (e) {
            console.warn(e);
        }
    };

    getUnlinkedDisclaimers = (disclaimerTypes = []) => {
        const disclaimers = disclaimerTypes
            .map(d => get(this.disclaimersDictionary, d))
            .filter(d => d !== undefined);

        return disclaimers;
    };

    getDisclaimersForDisplay(
        disclaimerTypes: Array<string> = [],
        includeSuperscriptedDisclaimers: boolean = false
    ) {
        const disclaimers: Array<any> = [];
        const disclaimersByType = disclaimerTypes.reduce(
            (acc, disclaimerType) => {
                const text = get(this.disclaimersDictionary, disclaimerType);
                if (text !== undefined) {
                    acc[disclaimerType] = text;
                }
                return acc;
            },
            {}
        );
        const superscriptDisclaimers: Array<string | object> = [];
        if (includeSuperscriptedDisclaimers && this.disclaimersByKey) {
            const orderedDisclaimers = Object.values(this.disclaimersByKey)
                .filter(disclaimer => {
                    const { superscript, type, text } = disclaimer;
                    if (superscript) {
                        if (disclaimersByType[type] === text) {
                            delete disclaimersByType[type];
                        }
                        return true;
                    }
                    return false;
                })
                .sort((a, b) => a.superscript - b.superscript)
                .map(disclaimer => ({
                    character: disclaimer.superscript.toString(),
                    content: disclaimer.text,
                }));

            superscriptDisclaimers.push(...orderedDisclaimers);
        }

        disclaimerTypes.forEach(disclaimerType => {
            if (disclaimersByType[disclaimerType]) {
                disclaimers.push(disclaimersByType[disclaimerType]);
            }
        });
        disclaimers.push(...superscriptDisclaimers);
        return disclaimers;
    }

    getDisclaimerTextFromEntity(entity: any, type: string): string | undefined {
        if (entity) {
            const { disclaimers: fundDisclaimers = {} } = entity;
            if (fundDisclaimers[type]) {
                const disclaimerForCulture = fundDisclaimers[type].find(
                    disclaimer => disclaimer.culture === this.culture
                );
                if (disclaimerForCulture) {
                    return disclaimerForCulture.disclaimer;
                }
            }
        }
        return undefined;
    }

    getDisclaimerTextForType(
        entity: any,
        cellDisclaimer: ConditionalDisclaimer,
        existingConditionalHelper?: ConditionalHelper
    ) {
        const { disclaimerType, conditional } = cellDisclaimer;
        if (conditional) {
            const conditionalHelper =
                existingConditionalHelper || new ConditionalHelper(conditional);
            if (!conditionalHelper.matches(entity)) {
                return undefined;
            }
        }
        let text: string | undefined = this.getDisclaimerTextFromEntity(
            entity,
            disclaimerType
        );
        // If there is no disclaimer at the entity level then look at the global disclaimers
        if (!text) {
            text =
                this.disclaimersDictionary &&
                this.disclaimersDictionary[disclaimerType];
        }
        return text;
    }

    getSuperscript(
        entity: any,
        cellDisclaimer: ConditionalDisclaimer
    ): number | undefined {
        return this.getDisclaimer<number>(
            entity,
            cellDisclaimer,
            DisclaimerDisplayType.superscript
        );
    }

    getSuperscripts(
        entity: any,
        cellDisclaimers: ConditionalDisclaimers
    ): Array<number> {
        return this.getDisclaimers<number>(
            entity,
            cellDisclaimers,
            DisclaimerDisplayType.superscript
        ).sort((a, b) => a - b);
    }

    getTooltipDisclaimer(
        entity: any,
        cellDisclaimer: ConditionalDisclaimer
    ): string | undefined {
        return this.getDisclaimer<string>(
            entity,
            cellDisclaimer,
            DisclaimerDisplayType.tooltip
        );
    }

    getTooltipDisclaimers(
        entity: any,
        cellDisclaimers: ConditionalDisclaimers
    ): Array<string> {
        return this.getDisclaimers<string>(
            entity,
            cellDisclaimers,
            DisclaimerDisplayType.tooltip
        );
    }

    private getDisclaimer<ReturnType>(
        entity: any,
        cellDisclaimer: ConditionalDisclaimer,
        targetDisclaimerDisplayType: DisclaimerDisplayType
    ): ReturnType | undefined {
        const { disclaimerType } = cellDisclaimer;
        const text = this.getDisclaimerTextForType(entity, cellDisclaimer);
        if (!text) {
            return undefined;
        }
        const key = this.getDisclaimerKey(disclaimerType, text);
        if (this.disclaimersByKey && this.disclaimersByKey[key]) {
            return targetDisclaimerDisplayType === DisclaimerDisplayType.tooltip
                ? this.disclaimersByKey[key].text
                : (this.disclaimersByKey[key].superscript as any);
        }
    }

    private getDisclaimers<ReturnType>(
        entity: any,
        cellDisclaimers: ConditionalDisclaimers,
        targetDisclaimerDisplayType: DisclaimerDisplayType
    ): Array<ReturnType> {
        const disclaimers = cellDisclaimers
            .filter(cellDisclaimer => {
                const disclaimerType =
                    cellDisclaimer.display || DisclaimerDisplayType.superscript;
                return disclaimerType === targetDisclaimerDisplayType;
            })
            .map(cellDisclaimer =>
                this.getDisclaimer<ReturnType>(
                    entity,
                    cellDisclaimer,
                    targetDisclaimerDisplayType
                )
            )
            .filter(disclaimer => !!disclaimer);
        return disclaimers as Array<any>;
    }
}



// WEBPACK FOOTER //
// ./src/stores/DisclaimerStore.tsx